'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import React, { useState, useMemo, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useGameweekNavigation } from '@/lib/context/GameweekNavigationProvider'
import { useAuth } from '@/lib/auth/AuthProvider'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Search,
  User,
  Palette,
  Sparkles,
  ArrowLeftRight,
  Menu
} from 'lucide-react'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import Image from 'next/image'
import InstanceSelectorDropdown from './desktop/InstanceSelectorDropdown'
import InstanceAwareTokenDisplay from './desktop/InstanceAwareTokenDisplay'
import { queryClient } from '@/lib/context/QueryProvider'
import { NTTopupPaymentModal } from '@/components/payment/NTTopupPaymentModal'
import { useInstanceContext } from '@/lib/context/InstanceProvider'
import { getPackageIconPath, getPackageTierLabel } from '@/lib/utils/ntTokenPackageIcons'
import { cn } from '@/lib/utils'
import { ThemeToggle } from './ThemeToggle'
import { TransferLayoutToggle } from './TransferLayoutToggle'
import { useNavTheme } from '@/lib/context/NavThemeContext'
import { useTheme } from 'next-themes'
import {
  getAuctionMarketNavPresentation,
  getPlatformMarketNavPresentation,
  type PlatformMarketNavState
} from '@/lib/transfers/platform-market-nav-label'

interface NavTokenPackage {
  id: string
  name: string
  base_tokens: number
  bonus_tokens: number
  total_tokens: number
  price_display: string
  value_proposition: string
  savings_message: string
  is_recommended: boolean
}

async function fetchNavTokenPackages(instanceId: string) {
  const response = await fetch(`/api/np-tokens/packages?instance_id=${instanceId}`)
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

function setTopupOpenFlag(isOpen: boolean) {
  if (typeof window !== 'undefined') {
    ;(window as any).__topup_open = isOpen
  }
}

export default function TopNavBar() {
  const pathname = usePathname()
  const isNavActive = (href: string) => {
    if (href === '/home') return pathname === '/home'
    if (href === '/transfers') return pathname === '/transfers'
    if (href === '/transfers/Platform') return pathname === '/transfers/Platform'
    return pathname?.startsWith(href) ?? false
  }
  const {
    selectedGameweek,
    canNavigatePrevious,
    canNavigateNext,
    navigateToPrevious,
    navigateToNext
  } = useGameweekNavigation()
  const { user, signOut } = useAuth()
  const { currentInstance, allInstances, refreshInstances } = useInstanceContext()
  const { cycleNavTheme, shineEnabled, toggleShine, useNewUI, toggleNewUI } = useNavTheme()
  const { resolvedTheme } = useTheme()
  const [themeMounted, setThemeMounted] = useState(false)
  useEffect(() => {
    setThemeMounted(true)
  }, [])
  // Default to dark logo for SSR consistency; swap once theme is resolved on the client.
  const logoSrc =
    themeMounted && resolvedTheme === 'light'
      ? '/assets/Logos/NPFCBlackLogo.png'
      : '/assets/Logos/NPFCwhiteLogo.png'
  const [showTopUp, setShowTopUp] = useState(false)
  const [requestedInstanceId, setRequestedInstanceId] = useState<string | null>(null)
  const [topUpPackages, setTopUpPackages] = useState<NavTokenPackage[]>([])
  const [topUpLoading, setTopUpLoading] = useState(false)
  const [topUpError, setTopUpError] = useState<string | null>(null)
  const [recommendedPackageId, setRecommendedPackageId] = useState<string | null>(null)
  const [topUpBalance, setTopUpBalance] = useState<number | null>(null)
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false)
  const [preselectedPackageId, setPreselectedPackageId] = useState<string | null>(null)
  const stripePurchaseCompletedRef = useRef(false)
  const [platformMarketNavState, setPlatformMarketNavState] =
    useState<PlatformMarketNavState | null>(null)
  const [platformMarketNow, setPlatformMarketNow] = useState(() => new Date())

  // Match live status

  const updateStripeFlowCompleted = (value: boolean) => {
    stripePurchaseCompletedRef.current = value
  }

  const activeInstance = useMemo(() => {
    if (requestedInstanceId) {
      return (
        allInstances.find((instance) => instance.id === requestedInstanceId) ||
        currentInstance ||
        null
      )
    }
    return currentInstance || null
  }, [requestedInstanceId, allInstances, currentInstance])

  useEffect(() => {
    if (!activeInstance?.id) {
      setPlatformMarketNavState(null)
      return
    }

    let cancelled = false
    const instanceId = activeInstance.id
    const instancePlayMode = activeInstance.play_mode

    setPlatformMarketNavState({
      playMode: instancePlayMode,
      platformMarket: undefined,
      marketplaceAuctions: undefined
    })

    const loadPlatformMarketState = async () => {
      try {
        const response = await fetch(
          `/api/play/transfers/market-state?instanceId=${encodeURIComponent(instanceId)}`,
          { cache: 'no-store' }
        )
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const payload = await response.json()
        const data = payload?.data ?? payload
        if (cancelled) return
        setPlatformMarketNavState({
          playMode: data?.playMode ?? instancePlayMode,
          platformMarket: data?.platformMarket ?? null,
          marketplaceAuctions: data?.marketplaceAuctions ?? null
        })
      } catch {
        if (cancelled) return
        setPlatformMarketNavState({
          playMode: instancePlayMode,
          platformMarket: null,
          marketplaceAuctions: null
        })
      }
    }

    loadPlatformMarketState()
    const interval = window.setInterval(loadPlatformMarketState, 60000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [activeInstance?.id, activeInstance?.play_mode])

  useEffect(() => {
    const interval = window.setInterval(() => setPlatformMarketNow(new Date()), 1000)
    return () => window.clearInterval(interval)
  }, [])

  const platformMarketNav = getPlatformMarketNavPresentation(
    platformMarketNavState ?? { playMode: activeInstance?.play_mode ?? null },
    platformMarketNow
  )
  const auctionMarketNav = getAuctionMarketNavPresentation(
    platformMarketNavState ?? { playMode: activeInstance?.play_mode ?? null }
  )

  const handleCloseTopUp = () => {
    setShowTopUp(false)
    setTopUpError(null)
    if (!isStripeModalOpen) {
      setPreselectedPackageId(null)
    }
    updateStripeFlowCompleted(false)
    setRequestedInstanceId(null)
    setTopupOpenFlag(false)
  }

  const handlePackageClick = (pkg: NavTokenPackage) => {
    if (!activeInstance) {
      return
    }
    setPreselectedPackageId(pkg.id)
    updateStripeFlowCompleted(false)
    setShowTopUp(false)
    setIsStripeModalOpen(true)
  }

  const handleStripeModalClose = () => {
    const shouldReopen = !stripePurchaseCompletedRef.current
    setIsStripeModalOpen(false)
    setPreselectedPackageId(null)
    if (shouldReopen) {
      setShowTopUp(true)
    } else {
      setShowTopUp(false)
      setRequestedInstanceId(null)
    }
    // Keep completion flag as-is; next open will explicitly reset it
  }

  const handlePurchaseComplete = (amount: number) => {
    // Mark completed so Stripe modal onClose won't reopen exchange
    updateStripeFlowCompleted(true)
    const newBalance = (topUpBalance ?? activeInstance?.nt_token_balance ?? 0) + amount
    // Optimistically bump local balance shown in the exchange
    setTopUpBalance(
      (prevBalance) => (prevBalance ?? activeInstance?.nt_token_balance ?? 0) + amount
    )
    setRequestedInstanceId(null)
    // Ensure global flag is cleared to avoid reopen
    setTopupOpenFlag(false)
    if (activeInstance?.id && user?.id) {
      queryClient.setQueryData(['instanceTokens', activeInstance.id, user.id], (prev: any) => {
        const previousData = prev ?? {}
        return {
          ...previousData,
          instanceTokens: newBalance,
          tokenType: previousData.tokenType ?? 'nt',
          tokenLabel: previousData.tokenLabel ?? 'NT',
          prizeTokens: previousData.prizeTokens ?? 0
        }
      })

      queryClient.setQueryData(['userInstances', user.id], (prev: any) => {
        if (!Array.isArray(prev)) return prev
        return prev.map((instance) => {
          if (instance.id !== activeInstance.id) return instance
          return { ...instance, nt_token_balance: newBalance }
        })
      })
    }
    // Refresh instance data so the top navbar token display updates
    // Note: We only invalidate userInstances here. The instanceTokens cache was already
    // updated optimistically above, and the 30-second auto-refetch in InstanceAwareTokenDisplay
    // will eventually sync. This avoids a race condition where immediate refetch would
    // read stale data from currentInstance before refreshInstances() completes.
    refreshInstances().catch(() => {})
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['userInstances', user.id] })
    }
  }

  useEffect(() => {
    const handler = (event: Event) => {
      const w = window as any
      if (w.__topup_open) return

      const detail = (event as CustomEvent<{ instanceId?: string }>).detail
      if (detail?.instanceId) {
        setRequestedInstanceId(detail.instanceId)
      } else if (currentInstance?.id) {
        setRequestedInstanceId(currentInstance.id)
      }

      w.__topup_open = true
      // Reset completion flag on open
      updateStripeFlowCompleted(false)
      setShowTopUp(true)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('openTopUp', handler as EventListener)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('openTopUp', handler as EventListener)
      }
    }
  }, [currentInstance?.id])

  // Keep a global flag in sync so other listeners won't react to the same event
  useEffect(() => {
    const w = window as any
    w.__topup_open = showTopUp || isStripeModalOpen
    return () => {
      if (w) {
        w.__topup_open = false
      }
    }
  }, [showTopUp, isStripeModalOpen])

  useEffect(() => {
    if (!showTopUp) {
      return
    }

    if (!activeInstance) {
      setTopUpError('Select a team instance to top up tokens.')
      setTopUpPackages([])
      return
    }

    // Load NT packages for paid tiers
    let cancelled = false

    const loadPackages = async () => {
      setTopUpLoading(true)
      setTopUpError(null)
      try {
        const data = await fetchNavTokenPackages(activeInstance.id)
        if (cancelled) return

        if (data.success && Array.isArray(data.packages)) {
          setTopUpPackages(data.packages)
          const recommended = data.recommendations?.suggested_package ?? null
          setRecommendedPackageId(recommended)
          setTopUpBalance(typeof data.current_balance === 'number' ? data.current_balance : null)
        } else {
          setTopUpError(data.error || 'Unable to load token packages.')
          setTopUpPackages([])
        }
      } catch (error) {
        if (cancelled) return
        const message = error instanceof Error ? error.message : 'Unable to load token packages.'
        setTopUpError(message)
        setTopUpPackages([])
      } finally {
        if (!cancelled) {
          setTopUpLoading(false)
        }
      }
    }

    loadPackages()
    return () => {
      cancelled = true
    }
  }, [showTopUp, activeInstance])

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-neu-surface" style={{ height: '74.77px' }}>
        <div
          className={cn(
            'relative h-full w-full items-center',
            useNewUI
              ? 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 px-2 sm:px-3 lg:px-4 xl:px-6 2xl:px-8'
              : 'flex justify-between px-6 lg:px-8'
          )}
        >
          {useNewUI ? (
            <>
              {/* NEW: Left Group — logo + team (money moved beside the theme toggle) */}
              <div className="flex min-w-0 items-center gap-1.5 justify-self-start lg:gap-2 xl:gap-4 2xl:gap-6">
                <Link
                  href="/"
                  aria-label="NeuroPlay Fantasy Championship"
                  className="flex shrink-0 items-center"
                >
                  <Image
                    src={logoSrc}
                    alt="NPFC"
                    width={180}
                    height={48}
                    className="h-7 w-auto lg:h-8 xl:h-10 2xl:h-11"
                    priority
                  />
                </Link>
                <InstanceSelectorDropdown />
              </div>

              {/* NEW: Center — primary nav links (moved out of the hidden blue bar) */}
              <nav className="flex min-w-0 items-center justify-center gap-x-1 lg:gap-x-2 xl:gap-x-6 2xl:gap-x-8">
                <HeaderNavLink href="/home" label="HOME" isActive={isNavActive('/home')} />
                <HeaderNavLink
                  href="/dashboard"
                  label="MY SQUAD"
                  isActive={isNavActive('/dashboard')}
                />
                {auctionMarketNav.isVisible && (
                  <HeaderNavLink
                    href="/transfers"
                    label={auctionMarketNav.label}
                    isActive={isNavActive('/transfers')}
                    isDisabled={auctionMarketNav.isDisabled}
                    title={auctionMarketNav.title}
                  />
                )}
                <HeaderNavLink
                  href="/transfers/Platform"
                  label={platformMarketNav.label}
                  isActive={isNavActive('/transfers/Platform')}
                  isDisabled={platformMarketNav.isDisabled}
                  title={platformMarketNav.title}
                />
                <HeaderNavLink
                  href="/elite-league"
                  label="ELITE LEAGUE"
                  isActive={isNavActive('/elite-league')}
                />
              </nav>
            </>
          ) : (
            <>
              {/* OLD: Left Group — burger + logo */}
              <div className="flex items-center gap-4">
                <button className="text-neu-text-primary">
                  <Menu className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-2">
                  <Link
                    href="/"
                    aria-label="NeuroPlay Fantasy Championship"
                    className="flex items-center pl-2"
                  >
                    <Image
                      src={logoSrc}
                      alt="NPFC"
                      width={180}
                      height={48}
                      className="sm:h-12"
                      style={{ width: 'auto', height: '3.5rem' }}
                      priority
                    />
                  </Link>
                </div>
              </div>

              {/* OLD: Center-right — GW + team + tokens (placed in right group below) */}
            </>
          )}

          {/* Right Group */}
          <div
            className={cn(
              'flex min-w-0 items-center justify-self-end',
              useNewUI ? 'gap-1.5 lg:gap-2 xl:gap-4 2xl:gap-6' : 'gap-6'
            )}
          >
            {/* OLD: GW pill + team + tokens sit on the right */}
            {!useNewUI && (
              <>
                <div className="flex items-center gap-2 rounded-lg bg-neu-border-soft px-3 py-1 text-neu-text-primary">
                  <button
                    onClick={navigateToPrevious}
                    disabled={!canNavigatePrevious}
                    className={cn(
                      'p-1 transition-colors',
                      !canNavigatePrevious
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:text-neu-text-secondary'
                    )}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="font-montserrat text-sm font-bold min-w-[3rem] text-center">
                    {selectedGameweek > 0 ? `GW ${selectedGameweek}` : '---'}
                  </div>
                  <button
                    onClick={navigateToNext}
                    disabled={!canNavigateNext}
                    className={cn(
                      'p-1 transition-colors',
                      !canNavigateNext
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:text-neu-text-secondary'
                    )}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <InstanceSelectorDropdown />
                <InstanceAwareTokenDisplay />
              </>
            )}

            {/* Icons */}
            <div
              className={cn(
                'flex min-w-0 items-center',
                useNewUI ? 'gap-1 lg:gap-2 xl:gap-3 2xl:gap-4' : 'gap-4'
              )}
            >
              {!useNewUI && (
                <button className="text-neu-text-primary p-1 border-2 border-neu-text-primary rounded-full">
                  <Search className="w-4 h-4" />
                </button>
              )}
              {/* Hidden for now — switch-UI toggle, nav color cycle, and shine toggle.
                  Restore this block to bring the controls back. */}
              {/*
              <button
                type="button"
                onClick={toggleNewUI}
                aria-label="Toggle new vs old UI"
                title={useNewUI ? 'Switch to old UI' : 'Switch to new UI'}
                className={cn(
                  'text-neu-text-primary transition-opacity hover:opacity-100',
                  useNewUI ? 'opacity-100' : 'opacity-50'
                )}
              >
                <ArrowLeftRight className="h-4 w-4 lg:h-5 lg:w-5" />
              </button>
              {useNewUI && (
                <>
                  <button
                    type="button"
                    onClick={cycleNavTheme}
                    aria-label="Cycle bar color"
                    className="text-neu-text-primary opacity-70 hover:opacity-100 transition-opacity"
                  >
                    <Palette className="h-4 w-4 lg:h-5 lg:w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={toggleShine}
                    aria-label="Toggle shine effect"
                    className={cn(
                      'text-neu-text-primary transition-opacity hover:opacity-100',
                      shineEnabled ? 'opacity-100' : 'opacity-50'
                    )}
                  >
                    <Sparkles className="h-4 w-4 lg:h-5 lg:w-5" />
                  </button>
                </>
              )}
              */}
              {/* Money / token balance — sits just left of the theme (light/dark) toggle */}
              {useNewUI && <InstanceAwareTokenDisplay />}
              {/* Transfer layout (1/2) toggle — only on the Transfer Market page */}
              {pathname === '/transfers' && (
                <TransferLayoutToggle className={useNewUI ? 'h-8 w-8 lg:h-9 lg:w-9' : undefined} />
              )}
              <ThemeToggle className={useNewUI ? 'h-8 w-8 lg:h-9 lg:w-9' : undefined} />
              <NotificationBell />
              {/* Profile */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-neu-text-primary border-2 border-neu-text-primary rounded-full p-1">
                      <User className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link href="/account">Account Settings</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => signOut()}>Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTopUp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background/80 dark:bg-black/70 transition-colors"
            onClick={handleCloseTopUp}
          />

          {/* Modal Container */}
          <div className="relative z-10 w-full max-w-[1404px] rounded-2xl border border-border bg-card/95 text-card-foreground shadow-2xl backdrop-blur-xl dark:bg-slate-950/90">
            {/* Close Button - top right as in Figma */}
            <button
              onClick={handleCloseTopUp}
              className="absolute right-6 top-6 p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close NT top-up"
            >
              <X className="w-[52px] h-[52px]" />
            </button>

            {/* Header with Icon and Title */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <div className="relative w-[66px] h-[58.56px]">
                <Image
                  src="/assets/coins/coin.png"
                  alt="Neurotoken"
                  fill
                  sizes="66px"
                  className="object-contain"
                />
              </div>
              <h1 className="font-['Montserrat'] font-extrabold italic text-[48px] md:text-[64px] leading-tight text-foreground uppercase tracking-[0.15px] text-center">
                NEUROTOKEN PACKAGE
              </h1>
            </div>

            {/* Content */}
            {!activeInstance ? (
              <div className="text-center text-muted-foreground py-16">
                Select an active team to top up Neurotokens.
              </div>
            ) : (
              <div>
                {topUpError && (
                  <div className="bg-red-500/10 border border-red-500/40 text-red-300 rounded-xl p-4 text-sm text-center mb-8">
                    {topUpError}
                  </div>
                )}

                {topUpLoading ? (
                  <div className="flex justify-center py-20">
                    <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-8 max-w-[1237px] mx-auto">
                    {topUpPackages.map((pkg) => {
                      // Determine tier styling - Bronze, Silver, Gold
                      const isGold = recommendedPackageId === pkg.id && !pkg.is_recommended
                      const isSilver = pkg.is_recommended
                      const isBronze = !isGold && !isSilver

                      return (
                        <div
                          key={pkg.id}
                          className={cn(
                            'relative',
                            isGold &&
                              'p-[2px] rounded-[30px] shadow-[0_0_70px_rgba(250,204,21,0.45)]'
                          )}
                        >
                          <div
                            className={cn(
                              'relative w-[373px] h-[552.5px] flex flex-col items-center justify-between py-8 px-6 transition-transform duration-300 hover:scale-105 rounded-[25px] bg-card text-card-foreground border border-border shadow-2xl shadow-black/10 dark:shadow-emerald-500/10',
                              isGold &&
                                'border-yellow-400/80 shadow-[0_0_80px_rgba(234,179,8,0.35)]',
                              isSilver &&
                                'shadow-[0_0_70px_rgba(148,163,184,0.4)] border border-border/70',
                              isBronze &&
                                'shadow-[0_0_60px_rgba(251,191,36,0.25)] border border-border/70'
                            )}
                          >
                            {/* Badge - exact Figma styling */}
                            {pkg.is_recommended && (
                              <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex flex-row justify-center items-center px-5 py-2.5 gap-2.5 w-[263px] h-[44px] bg-gradient-to-b from-slate-200 to-slate-500 rounded-[25px] text-slate-900">
                                <span className="font-['Montserrat'] font-bold italic text-[25px] leading-6 flex items-center text-center tracking-[0.15px]">
                                  MOST POPULAR!
                                </span>
                              </div>
                            )}
                            {!pkg.is_recommended && recommendedPackageId === pkg.id && (
                              <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex flex-row justify-center items-center px-5 py-2.5 gap-2.5 w-[212px] h-[44px] bg-gradient-to-b from-yellow-200 to-amber-500 rounded-[25px] text-amber-900">
                                <span className="font-['Montserrat'] font-bold italic text-[25px] leading-6 flex items-center text-center tracking-[0.15px]">
                                  BEST VALUE!
                                </span>
                              </div>
                            )}

                            {/* Coin Medal Icon - exact Figma size */}
                            <div className="w-[234px] h-[234px] flex items-center justify-center mt-4">
                              <Image
                                src={getPackageIconPath(pkg.name)}
                                alt={getPackageTierLabel(pkg.name)}
                                width={234}
                                height={234}
                                className="w-full h-full object-contain"
                              />
                            </div>

                            {/* Token Amount - exact Figma typography */}
                            <div
                              className={`
                              font-['Montserrat'] font-bold text-[80px] leading-none tracking-[0.15px] text-center
                              ${isGold ? 'bg-gradient-to-b from-yellow-200 to-amber-500 bg-clip-text text-transparent' : ''}
                            `}
                            >
                              {pkg.total_tokens.toLocaleString()}
                            </div>

                            {/* Bonus - exact Figma styling */}
                            <div className="flex items-center justify-center gap-2 -mt-2">
                              <Image
                                src={getPackageIconPath(pkg.name)}
                                alt={`${getPackageTierLabel(pkg.name)} Bonus`}
                                width={34}
                                height={34}
                                className="w-[34px] h-[34px]"
                              />
                              <span className="font-['Montserrat'] font-bold italic text-[30px] leading-6 text-foreground tracking-[0.15px]">
                                BONUS +{pkg.bonus_tokens.toLocaleString()}
                              </span>
                            </div>

                            {/* Purchase Button - exact Figma styling */}
                            <button
                              className="w-[329.51px] h-[42.56px] bg-primary text-primary-foreground rounded-lg flex items-center justify-center
                                        font-['Montserrat'] font-bold text-[30px] leading-6 tracking-[0.15px]
                                        hover:bg-primary/90 transition-all duration-200 active:scale-95 shadow-lg shadow-primary/30"
                              onClick={() => handlePackageClick(pkg)}
                            >
                              {pkg.price_display}
                            </button>
                          </div>
                        </div>
                      )
                    })}

                    {topUpPackages.length === 0 && !topUpError && (
                      <div className="text-center text-muted-foreground py-16">
                        No token packages available right now.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeInstance && (
        <NTTopupPaymentModal
          isOpen={isStripeModalOpen}
          onClose={handleStripeModalClose}
          onPurchaseComplete={(amount) => handlePurchaseComplete(amount)}
          instanceId={activeInstance.id}
          currentBalance={topUpBalance ?? activeInstance.nt_token_balance ?? 0}
          selectedPackageId={preselectedPackageId || null}
        />
      )}
    </>
  )
}

// In-header nav link (replaces the blue bar's NavItem). Uses theme-aware colors
// — the header background is light in light theme, so hardcoded white text
// (as the blue bar used) would be invisible. Inactive links are dimmed; the
// active route gets full opacity plus the underline bar.
function HeaderNavLink({
  href,
  label,
  isActive,
  isDisabled = false,
  title
}: {
  href: string
  label: string
  isActive: boolean
  isDisabled?: boolean
  title?: string
}) {
  const className = cn(
    'flex shrink-0 items-center justify-center rounded-lg px-2 py-2 font-montserrat text-[12px] font-bold uppercase leading-[20px] tracking-[0.15px] whitespace-nowrap transition-all duration-300 lg:px-2.5 lg:text-[13px] xl:px-3 xl:text-[15px] 2xl:px-4 2xl:text-[16px]',
    isDisabled
      ? 'cursor-not-allowed text-neu-text-muted opacity-40'
      : isActive
        ? 'bg-gradient-to-r from-neu-brand-from to-neu-brand-to text-white shadow-[0_10px_25px_rgba(7,113,185,0.45)]'
        : 'text-neu-text-primary opacity-60 hover:opacity-100'
  )

  if (isDisabled) {
    return (
      <span aria-disabled="true" className={className} title={title}>
        {label}
      </span>
    )
  }

  return (
    <Link href={href} className={className} title={title}>
      {label}
    </Link>
  )
}
